"use client";

import React, { useState, useEffect } from "react";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";
import type { ReservaB2C, ReservaB2B, NoShow } from "../../hooks/useApi";
import NoShowList from "./NoShowList";
import NoShowModal from "./NoShowModal";
import Button from "../ui/button/Button";
import { UserX, Calendar, Users, User, Hotel, FileText, DollarSign, Info, AlertTriangle, FileEdit, MapPin, Clock } from "lucide-react";

type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";

interface ReservaDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  reserva: ReservaB2C | ReservaB2B | null;
  type: "b2c" | "b2b";
  noShows?: NoShow[];
  userRole?: string;
  onRegisterNoShow?: (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }) => Promise<void>;
  onDeleteNoShow?: (id: number) => Promise<void>;
  onNoShowChange?: () => Promise<void>;
}

function getStatusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case "pending_payment":
      return "primary";
    case "partial_payment":
      return "info";
    case "paid":
      return "success";
    case "no_show":
      return "warning";
    case "completed":
      return "dark";
    default:
      return "light";
  }
}

const estadoDisplay = {
  pending_payment: "Pendiente",
  partial_payment: "Pago parcial",
  paid: "Pagada",
  no_show: "No Show",
  completed: "Completada"
};

export default function ReservaDetailsModal({
  isOpen,
  onClose,
  reserva,
  type,
  noShows = [],
  userRole = "worker",
  onRegisterNoShow,
  onDeleteNoShow,
  onNoShowChange
}: ReservaDetailsModalProps) {
  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  
  // Close NoShow modal when main modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsNoShowModalOpen(false);
    }
  }, [isOpen]);

  if (!reserva) return null;

  const isB2C = type === "b2c";
  
  // Directly access the properties we need based on the actual data structure
  let clientOrAgencyName = "Sin nombre";
  let clientEndNameValue = null;
  
  if (isB2C) {
    clientOrAgencyName = (reserva as ReservaB2C).clienteName || "Cliente sin nombre";
  } else {
    const b2bReservation = reserva as ReservaB2B;
    
    // Use type assertion to access properties that might not be in the interface
    const agencyObj = (b2bReservation as any).agency;
    
    // First try agency.name (from the nested agency object)
    if (agencyObj && agencyObj.name) {
      clientOrAgencyName = agencyObj.name;
    } 
    // Then try agenciaName
    else if (b2bReservation.agenciaName) {
      clientOrAgencyName = b2bReservation.agenciaName;
    }
    
    // Get client final name - directly access the property we know exists
    clientEndNameValue = b2bReservation.clientEndName;
  }
  
  const clientOrAgencyLabel = isB2C ? "Cliente" : "Agencia";
  
  // Get client final name with fallbacks
  const clientFinalName = !isB2C ? 
    ((reserva as ReservaB2B).clientEndName || 
     (reserva as any).clienteEndName || 
     (reserva as any).clientFinalName || 
     null) : null;

  // Determine if the user can register a NoShow based on reservation status
  const canRegisterNoShow = 
    onRegisterNoShow && 
    reserva.estado !== "no_show" && 
    reserva.estado !== "completed";

  // Handle opening the NoShow modal
  const handleOpenNoShowModal = () => {
    setIsNoShowModalOpen(true);
  };

  // Handle closing the NoShow modal
  const handleCloseNoShowModal = () => {
    setIsNoShowModalOpen(false);
  };

  // Handle the submission of a NoShow
  const handleSubmitNoShow = async (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }) => {
    if (onRegisterNoShow) {
      await onRegisterNoShow(data);
      
      // Refresh NoShow data if callback provided
      if (onNoShowChange) {
        await onNoShowChange();
      }
      
      setIsNoShowModalOpen(false);
    }
  };

  // Handle the deletion of a NoShow
  const handleDeleteNoShow = async (id: number) => {
    if (onDeleteNoShow) {
      await onDeleteNoShow(id);
      
      // Refresh NoShow data if callback provided
      if (onNoShowChange) {
        await onNoShowChange();
      }
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Detalles de la reserva"
        size="lg"
        className="max-w-4xl"
      >
        <div className="flex flex-col space-y-4 p-4">
          {/* Estado (status) - always at the top */}
          <div className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Info className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">Estado:</span>
            </div>
            <Badge color={getStatusBadgeColor(reserva.estado)}>
              {estadoDisplay[reserva.estado]}
            </Badge>
          </div>
          
          {/* Card-style sections stacked vertically */}
          <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <User className="w-4 h-4 mr-2 text-primary-500 dark:text-primary-400" />
                {isB2C ? "Información del cliente" : "Información de la agencia"}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{clientOrAgencyLabel}:</span>
                <span className="text-gray-900 dark:text-gray-100 break-words sm:text-right">{clientOrAgencyName}</span>
              </div>
              
              {/* Display client end name if this is a B2B reservation */}
              {!isB2C && clientEndNameValue && (
                <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Cliente final:</span>
                  <span className="text-gray-900 dark:text-gray-100 break-words sm:text-right">
                    {clientEndNameValue}
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Hotel:</span>
                <div className="flex items-center justify-end">
                  <Hotel className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100 break-words">
                    {reserva.hotel}
                  </span>
                </div>
              </div>

              {/* Display pickup time and location if available */}
              {reserva.hotelData && (
                <>
                  {reserva.hotelData.horarioRecogida && (
                    <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Horario de recogida:</span>
                      <div className="flex items-center justify-end">
                        <Clock className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span className="text-gray-900 dark:text-gray-100 break-words">
                          {reserva.hotelData.horarioRecogida}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {reserva.hotelData.lugarRecogida && (
                    <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Lugar de recogida:</span>
                      <div className="flex items-center justify-end">
                        <MapPin className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span className="text-gray-900 dark:text-gray-100 break-words">
                          {reserva.hotelData.lugarRecogida}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-primary-500 dark:text-primary-400" />
                Detalles de la excursión
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Excursión:</span>
                <span className="text-gray-900 dark:text-gray-100 break-words sm:text-right">{reserva.excursion}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Fecha:</span>
                <div className="flex items-center justify-end">
                  <Calendar className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100">{reserva.fecha}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Adultos:</span>
                <div className="flex items-center justify-end">
                  <Users className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100">{reserva.adultos}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Niños:</span>
                <div className="flex items-center justify-end">
                  <User className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100">{reserva.ninios}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <DollarSign className="w-4 h-4 mr-2 text-primary-500 dark:text-primary-400" />
                Información de pago
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Método:</span>
                <span className="text-gray-900 dark:text-gray-100 capitalize sm:text-right">{reserva.metodoPago}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Precio total:</span>
                <span className="text-gray-900 dark:text-gray-100 sm:text-right">${reserva.precioTotal.toFixed(2)}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Monto facturado:</span>
                <span className="text-gray-900 dark:text-gray-100 sm:text-right">${reserva.montoFacturado.toFixed(2)}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Saldo pendiente:</span>
                <div className="flex items-center justify-end">
                  <span className="text-gray-900 dark:text-gray-100">
                    {reserva.saldoPendiente <= 0 ? "$0.00" : `$${reserva.saldoPendiente.toFixed(2)}`}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Descuento:</span>
                <span className="text-gray-900 dark:text-gray-100 sm:text-right">{reserva.descuento}%</span>
              </div>

              {/* Display insurance information */}
              <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Seguro de viaje:</span>
                <span className="text-gray-900 dark:text-gray-100 sm:text-right">
                  {(reserva as any).incluyeSeguro ? "Sí" : "No"}
                </span>
              </div>
              
              {/* Mostrar información de ajuste manual si existe */}
              {(reserva as any).hasManualAdjustment && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                      Ajuste manual aplicado
                    </span>
                    {reserva.montoFacturado > reserva.precioTotal && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300">
                        Sobrepago: ${(reserva.montoFacturado - reserva.precioTotal).toFixed(2)}
                      </span>
                    )}
                  </div>
                  {(reserva as any).adjustmentReason && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line break-words">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Razón del ajuste:</span> {(reserva as any).adjustmentReason}
                    </p>
                  )}
                </div>
              )}

              {/* Mostrar comisión de agencia en las reservas B2B si existe */}
              {(() => {
                if (!isB2C) {
                  const comision = (reserva as ReservaB2B).agenciaComision;
                  if (comision !== undefined && comision > 0) {
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-[30%_70%] gap-1 sm:gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Comisión de agencia:</span>
                        <div className="text-right">
                          <span className="text-gray-900 dark:text-gray-100">${comision.toFixed(2)}</span>
                          <p className="text-xs text-blue-600 dark:text-blue-400">(incluida en el precio total)</p>
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })()}
            </div>
          </div>
          
          {/* No Shows section with Register button */}
          <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <UserX className="w-4 h-4 mr-2 text-amber-500 dark:text-amber-400" />
                Registro de No Shows
              </h3>
              {canRegisterNoShow && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenNoShowModal}
                  className="flex items-center space-x-1 text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  <UserX className="w-4 h-4" />
                  <span>Registrar No Show</span>
                </Button>
              )}
            </div>
            <div className="p-4">
              {noShows.length > 0 ? (
                <NoShowList 
                  noShows={noShows}
                  onDelete={userRole === "admin" ? handleDeleteNoShow : undefined}
                  userRole={userRole}
                />
              ) : (
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded-full mb-3">
                      <UserX className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-3">
                      No hay registros de No Shows para esta reserva
                    </p>
                    {canRegisterNoShow && (
                      <div className="mt-1 flex justify-center">
                        <button
                          onClick={handleOpenNoShowModal}
                          className="inline-flex items-center text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
                        >
                          <UserX className="w-3.5 h-3.5 mr-1" />
                          Registrar ausencia
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Notas adicionales (only if they exist) */}
          {reserva.notas && (
            <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                  <FileEdit className="w-4 h-4 mr-2 text-primary-500 dark:text-primary-400" />
                  Notas adicionales
                </h3>
              </div>
              <div className="p-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line break-words">{reserva.notas}</p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* NoShow Modal */}
      {canRegisterNoShow && (
        <NoShowModal
          isOpen={isNoShowModalOpen}
          onClose={handleCloseNoShowModal}
          reservationId={reserva.id}
          maxAdults={reserva.adultos}
          maxChildren={reserva.ninios}
          onSubmit={handleSubmitNoShow}
        />
      )}
    </>
  );
} 